import {Component, computed, inject, input, linkedSignal, OnInit, output, signal} from "@angular/core";
import {
	ApiError,
	ClientProperties,
	ErrorCodes,
	Event,
	EventState,
	MultiRoomRecordingSession,
	MultiRoomRecordingStation,
	Participant,
	Permissions,
	SessionState,
	SimUser,
	TranslationKey,
} from "@models";
import {EnumLabel, EnumLabelPipe} from "../../../../../../shared/shared-pipes/enum-label.pipe";
import {MatIcon} from "@angular/material/icon";
import {SessionStatusTagComponent} from "@common/components/session/session-status-tag/session-status-tag.component";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {ListItemComponent} from "@common/ui/list-item/list-item.component";
import {zoomInOnEnterAnimation} from "angular-animations";
import {SimTimeService} from "../../../../../../core/services/sim-services/sim-time.service";
import {SimButton} from "@common/ui/button/sim-button.component";
import {MatTooltip} from "@angular/material/tooltip";
import {SimDeviceMappingService, SimMaterialDialogService, SimSessionService, SimUserExtendedService, SimUtilityService} from "@services";
import {BusyDirective} from "@common/directives/busy.directive";
import {EventEmitterService} from "@event-emitter";
import {Router} from "@angular/router";
import {routes} from "../../../../../../../environments/routes";
import {UserListItemComponent} from "@common/ui/list-item/user-list-item/user-list-item.component";
import {TimeRangeLabelPipe} from "@common/pipes/time/time-range-label.pipe";
import {HasPermissionDirective} from "@common/directives/perm-directives/has-permission.directive";
import {NotificationService} from "@common/services/notification.service";
import Enumerable from "linq";

type StationWrapper = MultiRoomRecordingStation & {index: number};
type ParticipantWrapper = SimUser & {index: number};

@Component({
	selector: "sim-multi-room-recording-session-card",
	imports: [
		EnumLabelPipe,
		MatIcon,
		SessionStatusTagComponent,
		TranslateModule,
		ListItemComponent,
		SimButton,
		MatTooltip,
		BusyDirective,
		UserListItemComponent,
		TimeRangeLabelPipe,
		HasPermissionDirective,
	],
	animations: [zoomInOnEnterAnimation({duration: 150})],
	templateUrl: "./multi-room-recording-session-card.component.html",
	styleUrl: "./multi-room-recording-session-card.component.scss",
})
export class MultiRoomRecordingSessionCardComponent implements OnInit {
	// I/O
	public readonly inputSession = input.required<MultiRoomRecordingSession>({alias: "session"});
	public readonly event = input.required<Event>();
	public readonly showActionMenu = input<boolean>(false);
	public readonly disableControls = input<boolean>(false);
	public readonly onEdit = output<void>();
	public readonly onDelete = output<void>();

	// Constants
	protected readonly EnumLabel = EnumLabel;
	protected readonly T = Translations;
	protected readonly Permissions = Permissions;

	// Properties
	protected readonly timeFormat = inject(SimTimeService).timeFormat;
	protected readonly startPromise = signal<Promise<unknown> | false>(false);
	protected readonly abortPromise = signal<Promise<unknown> | false>(false);

	// Derived Properties
	protected readonly session = linkedSignal<MultiRoomRecordingSession>(() => this.inputSession());
	protected readonly stations = computed<StationWrapper[]>(() =>
		this.session().stations.map((station, index) => ({
			...station,
			index,
		})),
	);
	protected readonly participants = computed<ParticipantWrapper[]>(() =>
		this.session()
			.participants.filter((p: Participant) => p.user)
			.map((p: Participant, index) => ({...p.user, index})),
	);
	protected readonly canStartSession = computed<boolean>(
		() => this.event()?.state === EventState.Active && this.session().state === SessionState.Scheduled,
	);
	protected readonly isScheduledToday = computed<boolean>(
		() => !!SimUtilityService.toDayJs(this.session().startTime)?.isSame(new Date(), "day"),
	);
	protected readonly canOpenMasterOfCeremony = computed<boolean>(() => this.session().state === SessionState.Running);
	protected readonly canCancelSession = computed<boolean>(
		() => ![SessionState.Scheduled, SessionState.Aborted, SessionState.Finished].includes(this.session().state),
	);
	protected readonly canJoinAsExaminer = computed<boolean>(() =>
		this.computeCanJoinAsExaminer(this.session(), this.event(), this.clientProperties()),
	);
	protected readonly examinerDisabledTooltip = computed<TranslationKey | null>(() =>
		this.computeExaminerDisabledTooltip(this.canJoinAsExaminer(), this.clientProperties()),
	);

	// Injections
	readonly #sessionService = inject(SimSessionService);
	readonly #eventEmitter = inject(EventEmitterService);
	readonly #router = inject(Router);
	readonly #dialogService = inject(SimMaterialDialogService);
	readonly translate = inject(TranslateService);
	readonly #notificationService = inject(NotificationService);
	readonly #userService = inject(SimUserExtendedService);
	readonly clientProperties = SimDeviceMappingService.clientProperties;

	ngOnInit(): void {
		//Subscribe to session updates
		this.#eventEmitter.onTrainingSessionUpdated.subscribe((updatedSessionEvent) => {
			if (updatedSessionEvent.simSession.id === this.session()?.id) {
				this.session.set(updatedSessionEvent.simSession as MultiRoomRecordingSession);
			}
		});
	}

	protected startSession(): void {
		const promise = this.#sessionService.startMultiRoomRecordingSession(this.session().id).catch(async (error: ApiError) => {
			if (error.simError?.errorCode === ErrorCodes.RoomsInUseError) {
				await this.#dialogService.openManageBlockingSessionsDialog({sessionId: this.session().id});
			}
		});
		this.startPromise.set(promise);
	}

	protected async openMasterOfCeremonyApp(role: "mc" | "examiner"): Promise<void> {
		await this.#router.navigate([`${routes.routes.masterOfCeremonyApp}/multi-room-recording`, role, this.session().id]);
	}

	protected async cancelSession(): Promise<void> {
		const {action} = await this.#dialogService.openConfirmationDialog({
			title: this.T.CONFIRMATION_DIALOG_ABORT_SESSION_TITLE,
			cancelText: this.T.BUTTON_CANCEL,
			description: this.T.CONFIRMATION_DIALOG_ABORT_SESSION_DESC,
			okText: this.T.BUTTON_OK,
		});
		if (action === "ok") {
			const promise = this.#sessionService.abortSession({sessionId: this.session().id});
			this.abortPromise.set(promise);
			promise.then(() =>
				this.#notificationService.notifySuccess(this.translate.instant(Translations.CONFIRMATION_DIALOG_ABORT_SESSION_SUCCESS)),
			);
		}
	}

	private computeCanJoinAsExaminer(session: MultiRoomRecordingSession, event: Event, clientProperties: ClientProperties): boolean {
		const isHost = this.#userService.user.isHost;
		const isOwner = session.ownerUserId == this.#userService.user.id || event.ownerUserId == this.#userService.user.id;
		const hasPermissionToJoinAnyTrainingSession = this.#userService.hasPermission(Permissions.CanJoinAnyTrainingSession);
		const hasJoinClientPermissions =
			clientProperties.isTablet ||
			(clientProperties.isViewingStation && this.#userService.hasPermission(Permissions.CanJoinTrainingAsViewingStation));

		return (isOwner || hasPermissionToJoinAnyTrainingSession || isHost) && hasJoinClientPermissions;
	}

	private computeExaminerDisabledTooltip(canJoinAsExaminer: boolean, clientProperties: ClientProperties): TranslationKey | null {
		if (canJoinAsExaminer) {
			return null;
		}
		return clientProperties.isTablet || clientProperties.isViewingStation
			? Translations.MRR_SESSION_CARD_EXAMINER_DEVICE_TOOLTIP
			: Translations.MRR_SESSION_CARD_EXAMINER_PERMISSION_TOOLTIP;
	}
}
